import {
  useEffect,
  useRef,
  useState,
} from 'react'

type RecaptchaEnterpriseApi = {
  render: (
    container: HTMLElement,
    options: Record<string, unknown>,
  ) => number

  reset: (
    widgetId?: number,
  ) => void
}

declare global {
  interface Window {
    grecaptcha?: {
      enterprise?:
        RecaptchaEnterpriseApi
    }

    __diamondSuitesOcalaRecaptchaLoaded?:
      () => void
  }
}

const SCRIPT_ID =
  'diamond-suites-ocala-enterprise-recaptcha'

let scriptPromise:
  Promise<RecaptchaEnterpriseApi> |
  null = null

function getApi():
  RecaptchaEnterpriseApi |
  undefined {
  return window.grecaptcha
    ?.enterprise
}

function waitForApi(
  timeoutMs = 10000,
): Promise<RecaptchaEnterpriseApi> {
  return new Promise<
    RecaptchaEnterpriseApi
  >(
    (resolve, reject) => {
      const startedAt =
        Date.now()

      const check = () => {
        const api =
          getApi()

        if (api?.render) {
          resolve(api)
          return
        }

        if (
          Date.now() -
            startedAt >=
          timeoutMs
        ) {
          reject(
            new Error(
              'reCAPTCHA Enterprise did not initialize.',
            ),
          )

          return
        }

        window.setTimeout(
          check,
          50,
        )
      }

      check()
    },
  )
}

function loadRecaptcha():
  Promise<RecaptchaEnterpriseApi> {
  const current =
    getApi()

  if (current?.render) {
    return Promise.resolve(
      current,
    )
  }

  if (scriptPromise) {
    return scriptPromise
  }

  const loader =
    new Promise<
      RecaptchaEnterpriseApi
    >(
      (resolve, reject) => {
        const finish =
          () => {
            waitForApi()
              .then(resolve)
              .catch(reject)
          }

        window
          .__diamondSuitesOcalaRecaptchaLoaded =
          finish

        const existing =
          document.getElementById(
            SCRIPT_ID,
          )

        if (existing) {
          finish()
          return
        }

        const script =
          document.createElement(
            'script',
          )

        script.id =
          SCRIPT_ID

        script.src =
          'https://www.google.com/recaptcha/enterprise.js?onload=__diamondSuitesOcalaRecaptchaLoaded&render=explicit'

        script.async =
          true

        script.defer =
          true

        script.onerror =
          () => {
            script.remove()

            reject(
              new Error(
                'reCAPTCHA Enterprise could not be loaded.',
              ),
            )
          }

        document.head
          .appendChild(
            script,
          )
      },
    ).catch(
      (error) => {
        scriptPromise =
          null

        throw error
      },
    )

  scriptPromise =
    loader

  return loader
}

export default function Recaptcha({
  onTokenChange,
  resetKey,
}: {
  onTokenChange:
    (token: string) =>
      void

  resetKey:
    number
}) {
  const containerRef =
    useRef<HTMLDivElement>(
      null,
    )

  const widgetRef =
    useRef<number | null>(
      null,
    )

  const callbackRef =
    useRef(
      onTokenChange,
    )

  const previousResetKey =
    useRef(
      resetKey,
    )

  const [
    error,
    setError,
  ] =
    useState('')

  const siteKey =
    import.meta.env
      .VITE_RECAPTCHA_SITE_KEY
      ?.trim() ?? ''

  useEffect(() => {
    callbackRef.current =
      onTokenChange
  }, [
    onTokenChange,
  ])

  useEffect(() => {
    callbackRef.current(
      '',
    )

    setError('')

    if (
      !siteKey ||
      !containerRef.current
    ) {
      return
    }

    let cancelled =
      false

    loadRecaptcha()
      .then(
        (api) => {
          if (
            cancelled ||
            !containerRef.current ||
            widgetRef.current !==
              null
          ) {
            return
          }

          widgetRef.current =
            api.render(
              containerRef.current,
              {
                sitekey:
                  siteKey,

                theme:
                  'dark',

                callback:
                  (
                    token:
                      string,
                  ) => {
                    if (
                      cancelled
                    ) {
                      return
                    }

                    setError('')

                    callbackRef
                      .current(
                        token,
                      )
                  },

                'expired-callback':
                  () => {
                    callbackRef
                      .current(
                        '',
                      )
                  },

                'error-callback':
                  () => {
                    callbackRef
                      .current(
                        '',
                      )

                    setError(
                      'Security verification could not be completed. Please try again.',
                    )
                  },
              },
            )
        },
      )
      .catch(
        (
          loadError,
        ) => {
          console.error(
            'reCAPTCHA Enterprise load error:',
            loadError,
          )

          if (
            cancelled
          ) {
            return
          }

          setError(
            'Security verification could not be loaded. Please refresh and try again.',
          )
        },
      )

    return () => {
      cancelled =
        true
    }
  }, [
    siteKey,
  ])

  useEffect(() => {
    if (
      previousResetKey
        .current ===
      resetKey
    ) {
      return
    }

    previousResetKey.current =
      resetKey

    callbackRef.current(
      '',
    )

    setError('')

    const api =
      getApi()

    if (
      api &&
      widgetRef.current !==
        null
    ) {
      try {
        api.reset(
          widgetRef.current,
        )
      } catch (
        resetError
      ) {
        console.error(
          'reCAPTCHA Enterprise reset error:',
          resetError,
        )
      }
    }
  }, [
    resetKey,
  ])

  if (!siteKey) {
    return (
      <p className="field-error">
        Online security verification is
        not configured.
      </p>
    )
  }

  return (
    <div className="contact-form__recaptcha">
      <div
        ref={
          containerRef
        }
      />

      {error && (
        <p
          className="field-error"
          role="alert"
        >
          {error}
        </p>
      )}
    </div>
  )
}